import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, Send, AlertCircle, FileEdit, Search, Inbox as InboxIcon, ExternalLink, Paperclip, CheckCircle2 } from 'lucide-react';
import { DeliveryPill } from '../components/email/DeliveryPill';
import EmailThreadModal from '../components/email/EmailThreadModal';
import { API_BASE as API } from '../config';

/**
 * Cross-claim email inbox.
 *
 * One screen for triaging every email across every claim in the tenant —
 * the per-claim NotesModal Emails tab still exists for in-context replies,
 * but operators with dozens of active claims want a single feed to scan
 * after lunch instead of clicking into 30 separate claims.
 *
 * Filter tabs map 1:1 to the backend `direction` / `status` params so each
 * tab is a single fetch, no client-side filtering:
 *   • All        → no direction filter
 *   • Inbox      → direction=received
 *   • Sent       → direction=sent (excludes failed)
 *   • Failed     → status=failed
 *   • Drafts     → /api/email-drafts/all (separate collection)
 *
 * Clicking any row opens the existing EmailThreadModal so threading, reply,
 * "Save in claim", attachments, and the delivery pill all work without any
 * duplication. The modal needs a minimal `claim` object — we synthesize one
 * from the row's report_no + company_name (enough to gate the Save-in-claim
 * features the modal exposes).
 */
const TABS = [
  { id: 'inbox', label: 'Inbox', icon: InboxIcon, color: 'blue' },
  { id: 'all', label: 'All', icon: Mail, color: 'slate' },
  { id: 'sent', label: 'Sent', icon: Send, color: 'teal' },
  { id: 'failed', label: 'Failed', icon: AlertCircle, color: 'red' },
  { id: 'drafts', label: 'Drafts', icon: FileEdit, color: 'amber' },
];

const PAGE_SIZE = 50;

/** Build the backend query params for a given tab id. Pulled out so the
 *  effect dep array stays clean. */
const paramsForTab = (tab) => {
  if (tab === 'inbox') return { direction: 'received' };
  if (tab === 'sent') return { direction: 'sent' };
  if (tab === 'failed') return { status: 'failed' };
  return {};
};

const EmailInbox = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [threadEmailId, setThreadEmailId] = useState(null);
  const [threadToken, setThreadToken] = useState(null);
  const [threadClaim, setThreadClaim] = useState(null);
  const searchTimerRef = useRef(null);

  // Debounce the search input: every keystroke updates `search` immediately
  // for the controlled input, but the actual backend query only fires
  // ~350ms after the user stops typing. Without this, every character
  // would trigger a network round-trip.
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // Mark the inbox as "seen" once on mount so the navbar badge clears.
  // The unread counter only resets when the user actually opens this page;
  // tab switches inside the page do NOT re-mark (would be noisy and reset
  // unread state on every visit-back).
  useEffect(() => {
    axios.post(`${API}/inbox/mark-seen`).catch(() => { /* non-blocking */ });
    // Tell the navbar (which polls every 60s anyway) to refetch sooner.
    window.dispatchEvent(new CustomEvent('inbox:seen'));
  }, []);

  // Reset paging when filters change. Without this, switching tabs while
  // on page 3 would query page 3 of the new tab and show a confusing empty
  // state if the new tab has fewer rows.
  useEffect(() => {
    setOffset(0);
  }, [activeTab, debouncedSearch]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...paramsForTab(activeTab), limit: PAGE_SIZE, offset };
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await axios.get(`${API}/inbox`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedSearch, offset]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-drafts/all`);
      const all = res.data.items || [];
      const filtered = debouncedSearch
        ? all.filter((d) => {
            const hay = `${d.subject || ''} ${d.body_text || ''} ${(d.to || []).join(' ')}`.toLowerCase();
            return hay.includes(debouncedSearch.toLowerCase());
          })
        : all;
      setDrafts(filtered);
      setTotal(filtered.length);
    } catch {
      setDrafts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (activeTab === 'drafts') fetchDrafts();
    else fetchEmails();
  }, [activeTab, fetchEmails, fetchDrafts]);

  const openRow = (row) => {
    setThreadClaim({ report_no: row.report_no, company_name: row.company_name || '' });
    setThreadEmailId(row.email_id);
    setThreadToken(null);
  };

  const pageInfo = useMemo(() => {
    if (activeTab === 'drafts') return null;
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + items.length, total);
    return `${start}–${end} of ${total}`;
  }, [activeTab, offset, items.length, total]);

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="email-inbox-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <InboxIcon className="w-6 h-6 text-blue-600" />
              Email Inbox
            </h1>
            <p className="text-sm text-gray-500 mt-1">Every claim email in one place. No more drilling into individual claims to find replies.</p>
          </div>
          <Link
            to="/email/engagement"
            className="text-sm text-teal-700 hover:underline"
            data-testid="inbox-engagement-link"
          >
            View engagement stats →
          </Link>
        </div>

        {/* Tabs + Search */}
        <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
          <div className="flex flex-wrap items-center gap-1 px-3 pt-2 border-b border-gray-100">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px inline-flex items-center gap-1.5 ${
                    isActive
                      ? `border-${t.color}-500 text-${t.color}-700`
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  style={isActive ? { borderColor: tabColor(t.color, 500), color: tabColor(t.color, 700) } : undefined}
                  data-testid={`inbox-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject, body, sender, recipient…"
              className="flex-1 text-sm bg-transparent border-none focus:outline-none px-1 py-1"
              data-testid="inbox-search-input"
            />
            {pageInfo && <span className="text-xs text-gray-400 px-2" data-testid="inbox-page-info">{pageInfo}</span>}
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-12" data-testid="inbox-loading">Loading…</p>
          ) : activeTab === 'drafts' ? (
            drafts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-12" data-testid="inbox-empty-drafts">No drafts. Start an email on any claim and it auto-saves here.</p>
            ) : (
              drafts.map((d) => (
                <DraftRow key={d.draft_id} d={d} />
              ))
            )
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-12" data-testid="inbox-empty">
              {activeTab === 'inbox' ? 'No replies yet. Sent emails will show responses here as they arrive.'
                : activeTab === 'sent' ? 'No sent emails on this tenant.'
                : activeTab === 'failed' ? 'No failed sends — your delivery is clean.'
                : 'No emails match your filters.'}
            </p>
          ) : (
            items.map((row) => <InboxRow key={row.email_id} row={row} onOpen={() => openRow(row)} />)
          )}
        </div>

        {/* Pagination — drafts have no pagination, they're capped server-side at 200 */}
        {activeTab !== 'drafts' && total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              data-testid="inbox-prev-page"
            >
              ← Previous
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + items.length >= total}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
              data-testid="inbox-next-page"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <EmailThreadModal
        open={!!threadEmailId || !!threadToken}
        onClose={() => { setThreadEmailId(null); setThreadToken(null); setThreadClaim(null); }}
        emailId={threadEmailId}
        threadToken={threadToken}
        claim={threadClaim}
      />
    </div>
  );
};

/** Resolve tailwind colour numerics to actual hex codes for inline styles.
 *  We can't use string-interpolated tailwind classes (`border-${color}-500`)
 *  because the JIT compiler can't see them and they get purged. */
function tabColor(name, weight) {
  const palette = {
    blue:  { 500: '#3b82f6', 700: '#1d4ed8' },
    slate: { 500: '#64748b', 700: '#334155' },
    teal:  { 500: '#14b8a6', 700: '#0f766e' },
    red:   { 500: '#ef4444', 700: '#b91c1c' },
    amber: { 500: '#f59e0b', 700: '#b45309' },
  };
  return palette[name]?.[weight];
}

/**
 * Strip the noise off a body preview before we render it in a row:
 *   • Quoted reply chain — anything from a "From: " / "On <date>… wrote:" /
 *     ">…" line onward. Replies email the original message back and our
 *     preview pipeline captures all of it.
 *   • Signature block — anything after a Markdown-ish separator
 *     ("--", "___", "—") OR a recognisable signature lead-in
 *     ("Sent from my iPhone", contact lines starting with "E:" / "M:").
 *
 * Patterns are whitespace-anchored (not just \n) because body_preview is
 * commonly a single line where the backend has collapsed newlines into
 * spaces during snippet generation. Intentionally conservative: if we
 * can't find a clean break we return the original. Users still see the
 * raw text in EmailThreadModal when they click in. The goal is just a
 * calmer list row.
 */
function cleanBodyPreview(raw) {
  if (!raw) return '';
  const s = String(raw);
  // All markers use (?:^|\s) so they match at start-of-string OR after any
  // whitespace (newline OR plain space, since body_preview is often single-line).
  const markers = [
    // Quoted reply / forward chain
    /(?:^|\s)From:\s/i,
    /(?:^|\s)On\s.{1,80}wrote:/i,
    /(?:^|\s)-{2,}\s*Original Message\s*-{2,}/i,
    /(?:^|\s)>+\s/,
    // Signature separators / sentinels
    /(?:^|\s)-{2,}(?:\s|$)/,        // "--" surrounded by whitespace
    /(?:^|\s)_{3,}/,                // "___" underscore divider
    /(?:^|\s)—{2,}/,                // "——" em-dash divider
    /(?:^|\s)Sent from my /i,
  ];
  let cutAt = s.length;
  for (const rx of markers) {
    const m = s.search(rx);
    if (m >= 0 && m < cutAt) cutAt = m;
  }
  return s.slice(0, cutAt).trim();
}

/** One row in the inbox list. Mirrors the per-claim Emails tab card but
 *  adds claim context (company + report-no chip) since the user is looking
 *  across many claims at once. */
const InboxRow = ({ row, onOpen }) => {
  const isInbound = row.direction === 'received';
  const ts = row.sent_at || row.created_at;
  const hasAtts = (row.attachments || []).length > 0;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors group select-none ${
        isInbound ? 'bg-blue-50/30' : ''
      }`}
      data-testid={`inbox-row-${row.email_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Direction icon */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          row.status === 'failed' ? 'bg-red-100'
          : isInbound ? 'bg-blue-100'
          : 'bg-teal-100'
        }`}>
          {row.status === 'failed' ? <AlertCircle className="w-4 h-4 text-red-600" />
            : isInbound ? <Mail className="w-4 h-4 text-blue-600" style={{ transform: 'scaleX(-1)' }} />
            : <CheckCircle2 className="w-4 h-4 text-teal-600" />}
        </div>

        <div className="flex-1 min-w-0">
          {/* Subject + timestamp */}
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <div className="flex items-baseline gap-2 min-w-0">
              {isInbound && <span className="text-[10px] uppercase tracking-wide bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded flex-shrink-0">Reply</span>}
              <span className="text-sm font-semibold text-gray-800 truncate">{row.subject || '(no subject)'}</span>
            </div>
            <span className="text-[11px] text-gray-400 flex-shrink-0">
              {ts ? new Date(ts).toLocaleString() : ''}
            </span>
          </div>

          {/* Claim context — clickable to jump to the claim's notes modal */}
          <div className="flex items-center gap-2 mb-1 text-xs">
            <Link
              to={`/all-entries?open_emails=${encodeURIComponent(row.report_no)}${row.loss_type ? `&loss_type=${row.loss_type}&notes_tab=emails` : ''}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
              data-testid={`inbox-row-claim-${row.email_id}`}
            >
              <ExternalLink className="w-3 h-3" />
              {row.company_name || row.report_no}
              {row.loss_type && <span className="uppercase text-gray-400 ml-1">· {row.loss_type}</span>}
            </Link>
          </div>

          {/* From / To */}
          <p className="text-xs text-gray-500 truncate max-w-2xl">
            {isInbound ? `From: ${row.from || ''}` : `To: ${(row.to || []).join(', ')}`}
          </p>

          {/* Body preview — cleaned of signature & quoted-reply chain, and
              width-capped so a long unbroken line doesn't stretch the row
              all the way to the right edge of the viewport. */}
          {row.body_preview && (() => {
            const clean = cleanBodyPreview(row.body_preview);
            if (!clean) return null;
            return <p className="text-xs text-gray-500 truncate mt-0.5 max-w-2xl">{clean}</p>;
          })()}

          {/* Meta row */}
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
            <span>By {row.created_by_name || '—'}</span>
            {hasAtts && (
              <span className="inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                {row.attachments.length}
              </span>
            )}
            {row.status === 'failed' && <span className="text-red-500 font-medium">Failed</span>}
            <DeliveryPill msg={row} />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Draft row — separate from InboxRow because drafts aren't emails (they
 *  haven't been sent yet) so there's no thread to open. Clicking jumps to
 *  the claim's NotesModal opened on the Drafts tab, which is where the
 *  resume-draft flow lives. */
const DraftRow = ({ d }) => {
  const preview = cleanBodyPreview(d.body_text || '').slice(0, 120);
  return (
    <Link
      to={`/all-entries?open_emails=${encodeURIComponent(d.report_no)}&notes_tab=drafts${d.loss_type ? `&loss_type=${d.loss_type}` : ''}`}
      className="block px-4 py-3 hover:bg-amber-50/40 transition-colors"
      data-testid={`inbox-draft-row-${d.draft_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-100 flex-shrink-0">
          <FileEdit className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-[10px] uppercase tracking-wide bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded flex-shrink-0">Draft</span>
              <span className="text-sm font-semibold text-gray-800 truncate">{d.subject || '(no subject)'}</span>
            </div>
            <span className="text-[11px] text-gray-400 flex-shrink-0">{d.updated_at ? new Date(d.updated_at).toLocaleString() : ''}</span>
          </div>
          <div className="text-xs mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700">
              {d.company_name || d.report_no}
              {d.loss_type && <span className="uppercase text-gray-400 ml-1">· {d.loss_type}</span>}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate max-w-2xl">
            To: {(d.to || []).join(', ') || <span className="italic">no recipient yet</span>}
          </p>
          {preview && <p className="text-xs text-gray-500 truncate mt-0.5 max-w-2xl">{preview}</p>}
        </div>
      </div>
    </Link>
  );
};

export default EmailInbox;
